import React, { useEffect, useState, useMemo, useCallback } from "react";
import { isAxiosError } from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Container,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence, Variants } from "framer-motion";
import useApiClient from "../../api/Client";
import ENDPOINTS from "../../api/endpoints";
import Icons from "@/assets";
import { clearAccessToken } from "@/api/tokenManager";
import { debounce } from "lodash";

type StatusType =
  | "loading"
  | "success"
  | "already_verified"
  | "invalid"
  | "expired"
  | "error"
  | "change_success";

interface StatusConfig {
  icon: React.ReactNode;
  message: string;
  subMessage: string;
  buttonText: string;
  buttonAction: string;
}

const ICON_VARIANTS: Variants = {
  initial: { scale: 0, rotate: -45 },
  animate: {
    scale: 1,
    rotate: 0,
    transition: { type: "spring", damping: 12, stiffness: 200 },
  },
};

const StyledContainer = styled(Container)({
  backgroundColor: "#F0EAFF",
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
});

const ContentBox = styled(motion.div)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: "400px",
});

const IconWrapper = styled(motion.div)(({ theme }) => ({
  height: "19.42vw",
  width: "19.42vw",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  marginBottom: "11.65vw",
  [theme.breakpoints.up("sm")]: {
    height: "15.36vw",
    width: "15.36vw",
    marginBottom: "4vw",
  },
}));

// --- Config Mapping ---
const STATUS_CONFIG: Record<Exclude<StatusType, "loading">, StatusConfig> = {
  success: {
    icon: (
      <img
        src={Icons.EmailVerified}
        alt="Success"
        style={{ width: "100%", height: "100%" }}
      />
    ),
    message: "Email verified successfully.",
    subMessage: "You can now login to your account.",
    buttonText: "Go to Login",
    buttonAction: "/",
  },
  already_verified: {
    icon: (
      <img
        src={Icons.AlreadyVerified}
        alt="Already Verified"
        style={{ width: "100%", height: "100%" }}
      />
    ),
    message: "Your email is already verified.",
    subMessage: "You can now login to your account.",
    buttonText: "Go to Login",
    buttonAction: "/",
  },
  invalid: {
    icon: (
      <img
        src={Icons.NotVerified}
        alt="Invalid"
        style={{ width: "100%", height: "100%" }}
      />
    ),
    message: "Your verification link is invalid.",
    subMessage: "Please try again later.",
    buttonText: "Go to Login",
    buttonAction: "/",
  },
  expired: {
    icon: (
      <img
        src={Icons.Expired}
        alt="Expired"
        style={{ width: "100%", height: "100%" }}
      />
    ),
    message: "Your verification link has expired.",
    subMessage: "Please request a new verification link.",
    buttonText: "Resend Link",
    buttonAction: "/register",
  },
  error: {
    icon: (
      <img
        src={Icons.Alert}
        alt="Error"
        style={{ width: "100%", height: "100%" }}
      />
    ),
    message: "Unable to verify at the moment.",
    subMessage: "Please try again later.",
    buttonText: "Go to Login",
    buttonAction: "/",
  },
  change_success: {
    icon: (
      <img
        src={Icons.EmailVerified}
        alt="Success"
        style={{ width: "100%", height: "100%" }}
      />
    ),
    message: "Email updated successfully.",
    subMessage: "You can now safely close this tab and return to your profile.",
    buttonText: "Close Tab",
    buttonAction: "",
  },
};

const StatusIcon = React.memo(
  ({ status }: { status: Exclude<StatusType, "loading"> }) => (
    <IconWrapper variants={ICON_VARIANTS} initial="initial" animate="animate">
      {STATUS_CONFIG[status].icon}
    </IconWrapper>
  ),
);

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { api } = useApiClient();
  const token = searchParams.get("token");
  const type = searchParams.get("type");
  const [status, setStatus] = useState<StatusType>("loading");
  const [customMessage, setCustomMessage] = useState<string | null>(null);
  const [targetApp, setTargetApp] = useState<string>("contribute");
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleGoToLogin = async () => {
    setIsRedirecting(true);
    try {
      const fcm_token = localStorage.getItem("fcm_token");
      await api.post(ENDPOINTS.AUTH.LOGOUT, { fcm_token });
    } catch (e) {
      console.error("Logout failed during redirection from verify-email", e);
    } finally {
      clearAccessToken();
      [
        "fcm_token",
        "hasSeenAccessibilityGuide",
        "from_explore_to_contribute",
        "update_reason",
        "profileTab",
      ].forEach((item) => localStorage.removeItem(item));

      setTimeout(() => {
        window.location.href = `/${targetApp}/`;
      }, 100);
    }
  };

  const performVerification = useCallback(debounce(async () => {
    if (!token) {
      setStatus("invalid");
      return;
    }

    try {
      const endpoint = type === "change" 
        ? `${ENDPOINTS.PROFILE.VERIFY_EMAIL_CHANGE}?token=${token}`
        : `${ENDPOINTS.AUTH.VERIFY_EMAIL}?token=${token}`;

      const { data } = await api.get(endpoint);
      const code = data?.data?.code;
      if (data?.data?.app) setTargetApp(data.data.app);

      if (code === "ALREADY_VERIFIED") {
        setStatus("already_verified");
      } else {
        setStatus(type === "change" ? "change_success" : "success");
      }
      } catch (err: unknown) {
      if (isAxiosError(err)) {
        const responseData = err?.response?.data;
        const code = responseData?.data?.code;
        const msg = responseData?.message;
        if (responseData?.data?.app) setTargetApp(responseData.data.app);

        if (code === "TOKEN_EXPIRED") {
          setStatus("expired");
        } else if (code === "TOKEN_INVALID" || code === "TOKEN_MISSING") {
          setStatus("invalid");
        } else if (code === "ALREADY_VERIFIED") {
          setStatus("already_verified");
        } else {
          setStatus("error");
          if (msg) setCustomMessage(msg);
        }
      } else {
        setStatus("error");
      }
    }
  }, 5000, { leading: true, trailing: false }), [api, token, type]);

  useEffect(() => {
    const timer = setTimeout(performVerification, 1500);
    return () => {
      clearTimeout(timer);
      performVerification.cancel();
    };
  }, [performVerification]);

  const config = useMemo(() => {
    if (status === "loading") return null;
    return STATUS_CONFIG[status];
  }, [status]);

  return (
    <StyledContainer maxWidth={false}>
      <Box
        sx={{
          mt: { xs: "33.99vw", sm: "3.13vw" },
          mb: { xs: "32.2vw", sm: "3.78vw" },
        }}
      >
        <Box
          sx={{
            width: { xs: "40.5vw", sm: "20.83vw" },
            height: { xs: "11.65vw", sm: "5.56vw" },
          }}
        >
          <img
            src={Icons.Logo}
            alt="logo"
            style={{ height: "100%", width: "100%" }}
          />
        </Box>
      </Box>

      <AnimatePresence mode="wait">
        <ContentBox
          key={status}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          {status === "loading" ? (
            <Box sx={{ p: "5%" }}>
              <CircularProgress
                size={50}
                sx={{ color: "#8861F7", mb: "20%" }}
              />
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: "3.88vw", sm: "1.67vw" }, color: "#8861F7" }}
              >
                Verifying...
              </Typography>
            </Box>
          ) : (
            config && (
              <>
                <StatusIcon status={status} />

                <Typography
                  variant="h5"
                  sx={{
                    color: "#191919",
                    fontWeight: 600,
                    fontSize: { xs: "3.88vw", sm: "1.67vw" },
                  }}
                >
                  {customMessage || config.message}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mt: { xs: "0.97vw", sm: "2vw" },
                    color: "#626262",
                    fontWeight: 500,
                    fontSize: { xs: "3.88vw", sm: "1.25vw" },
                  }}
                >
                  {config.subMessage}
                </Typography>

                {status !== "error" && (
                  <Button
                    sx={{
                      borderRadius: "50px",
                      py: "3%",
                      mt: { xs: "8.74vw", sm: "2.5vw" },
                      fontWeight: 500,
                      fontSize: { xs: "3.88vw", sm: "1.11vw" },
                      color: "#FFFFFF",
                      boxShadow: "0px 13px 30px 0px #A485FD66",
                      "&:hover": {
                        backgroundImage:
                          "linear-gradient(180deg, #8861F7 0%, #6333F9 100%)",
                        boxShadow: "0 12px 24px rgba(119, 71, 251, 0.4)",
                      },
                    }}
                    fullWidth
                    variant="contained"
                    disabled={isRedirecting}
                    onClick={
                      status === "change_success" ? () => window.close() :
                      status === "expired" ? () => window.location.href = `/` : handleGoToLogin
                    }
                  >
                    {isRedirecting ? "Loading..." : config.buttonText}
                  </Button>
                )}
              </>
            )
          )}
        </ContentBox>
      </AnimatePresence>
    </StyledContainer>
  );
};

export default EmailVerification;
